package features.booking

import kotlin.math.roundToInt

/**
 * Cálculo de precio del lado del cliente — función pura y testeable.
 * Debe coincidir con lib/calculos.js (calcularTotal) del servidor; el servidor
 * recalcula y es la fuente de verdad, esto es solo para mostrar al usuario.
 */
object CalculoPrecios {
    const val PRECIO_BASE_DEFAULT = 350
    const val PRECIO_MALETA_DEFAULT = 150
    const val PRECIO_MASCOTA_DEFAULT = 150
    const val IVA = 0.16

    // Recargo cuando el cobro pasa por Stripe (cubre su comisión). NO aplica en
    // transferencia manual ni efectivo — el dinero llega directo, sin comisión.
    // Este es el DEFAULT: el valor real vive en catalogos/precios/costoServicio,
    // editable desde el panel de admin. El servidor recalcula y es la fuente de verdad.
    const val COSTO_SERVICIO_STRIPE = 12
    private val METODOS_CON_RECARGO = setOf("spei", "oxxo", "card")

    /**
     * Costo por servicio según método de pago.
     * @param precios catálogo; si trae "costoServicio" se usa ese valor (el admin puede
     *   ponerlo en 0 para no cobrar recargo). Si no viene, se usa COSTO_SERVICIO_STRIPE.
     */
    fun costoServicioPorMetodo(metodoPago: String, precios: Map<String, Int> = emptyMap()): Int {
        if (!METODOS_CON_RECARGO.contains(metodoPago.lowercase())) return 0
        val configurado = precios["costoServicio"]
        return if (configurado != null && configurado >= 0) configurado else COSTO_SERVICIO_STRIPE
    }

    /**
     * @param pasajeros lista de pasajeros con maletasExtras y llevaMascota
     * @param precios catálogo {"base","maleta","mascota"} (usa defaults si falta)
     * @param esIdaYVuelta duplica el subtotal
     * @param requiereFactura agrega 16% IVA redondeado
     * @return total en pesos
     */
    fun calcularTotal(
        pasajeros: List<PasajeroInfo>,
        precios: Map<String, Int>,
        esIdaYVuelta: Boolean,
        requiereFactura: Boolean
    ): Int {
        val precioBase = precios["base"] ?: PRECIO_BASE_DEFAULT
        val precioMaleta = precios["maleta"] ?: PRECIO_MALETA_DEFAULT
        val precioMascota = precios["mascota"] ?: PRECIO_MASCOTA_DEFAULT

        var totalPorTramo = 0
        pasajeros.forEach { p ->
            val costoMaletas = p.maletasExtras * precioMaleta
            val costoMascota = if (p.llevaMascota) precioMascota else 0
            totalPorTramo += precioBase + costoMaletas + costoMascota
        }

        val subtotal = if (esIdaYVuelta) totalPorTramo * 2 else totalPorTramo
        val iva = if (requiereFactura) (subtotal * IVA).roundToInt() else 0
        return subtotal + iva
    }

    /**
     * Total de una compra ida y vuelta donde cada tramo tiene SUS PROPIOS pasajeros/extras
     * (maletas/mascota pueden diferir entre ida y vuelta). El servidor recalcula.
     */
    fun calcularTotalIdaVuelta(
        pasajerosIda: List<PasajeroInfo>,
        pasajerosVuelta: List<PasajeroInfo>,
        precios: Map<String, Int>,
        requiereFactura: Boolean
    ): Int {
        val ida = calcularTotal(pasajerosIda, precios, false, false)
        val vuelta = calcularTotal(pasajerosVuelta, precios, false, false)
        val subtotal = ida + vuelta
        val iva = if (requiereFactura) (subtotal * IVA).roundToInt() else 0
        return subtotal + iva
    }
}
